import { useState } from "react";
import { commentList } from "@/mocks/homeData";
import type { CommentItem } from "@/pages/home/types";
import SectionHeading from "@/pages/home/components/SectionHeading";

export default function CommentWall() {
  const [comments, setComments] = useState<CommentItem[]>(commentList);
  const [liked, setLiked] = useState<Record<string, boolean>>({});
  const [draft, setDraft] = useState("");

  const toggleLike = (id: string) => {
    setLiked((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleAdd = () => {
    const content = draft.trim();
    if (!content) return;
    setComments((prev) => [
      {
        id: `local-${Date.now()}`,
        user: "我",
        initial: "我",
        time: "刚刚",
        content: content.slice(0, 200),
        likes: 0,
      },
      ...prev,
    ]);
    setDraft("");
  };

  return (
    <section id="comments" className="w-full px-4 py-20 md:px-8 md:py-28">
      <SectionHeading
        kicker="吃瓜热评区"
        title="评论区才是主战场"
        desc="这里的氛围比正文更热烈：时间线、上下文、对照截图，很多细节都是围观者自己补齐的。
          登录后即可参与讨论，当前为演示状态。"
        side={
          <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-4 py-2 font-label text-xs text-primary-700">
            <i className="ri-chat-3-line" />
            本期 12,846 条讨论
          </span>
        }
      />

      <div className="mt-12 grid grid-cols-1 gap-6 md:mt-16 lg:grid-cols-12">
        <div className="lg:col-span-8">
          <div className="rounded-lg border border-background-300 bg-background-100 p-5 md:p-6">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-500 font-heading text-base font-bold text-background-50">
                我
              </span>
              <input
                type="text"
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                placeholder="说点什么，补充线索或指出遗漏…"
                className="h-10 flex-1 rounded-md border border-background-300 bg-background-50 px-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
              />
              <button
                type="button"
                onClick={handleAdd}
                className="hidden h-10 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 font-label text-sm text-background-50 transition-colors hover:bg-primary-600 sm:inline-flex"
              >
                <i className="ri-send-plane-line" />
                发布
              </button>
            </div>
            <button
              type="button"
              onClick={handleAdd}
              className="mt-3 inline-flex h-10 w-full cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 font-label text-sm text-background-50 sm:hidden"
            >
              <i className="ri-send-plane-line" />
              发布评论
            </button>
          </div>

          <ul className="mt-6 flex flex-col gap-4">
            {comments.map((comment: CommentItem) => {
              const isLiked = Boolean(liked[comment.id]);
              const likeCount = comment.likes + (isLiked ? 1 : 0);
              return (
                <li
                  key={comment.id}
                  className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary-100 font-heading text-base font-bold text-secondary-800">
                    {comment.initial}
                  </span>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="font-heading text-sm font-bold text-foreground-900">
                        {comment.user}
                      </span>
                      <span className="inline-flex items-center gap-1 font-label text-[11px] text-foreground-400">
                        <i className="ri-time-line" />
                        {comment.time}
                      </span>
                    </div>
                    <p className="mt-2 text-[13px] leading-relaxed text-foreground-700 md:text-sm">
                      {comment.content}
                    </p>
                    <div className="mt-3 flex items-center gap-5">
                      <button
                        type="button"
                        onClick={() => toggleLike(comment.id)}
                        className={`inline-flex cursor-pointer items-center gap-1.5 font-label text-xs transition-colors ${
                          isLiked ? "text-primary-600" : "text-foreground-500 hover:text-primary-600"
                        }`}
                      >
                        <i className={isLiked ? "ri-thumb-up-fill" : "ri-thumb-up-line"} />
                        {likeCount}
                      </button>
                      <span className="inline-flex items-center gap-1.5 font-label text-xs text-foreground-500">
                        <i className="ri-chat-3-line" />
                        回复
                      </span>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        <aside className="lg:col-span-4">
          <div className="rounded-lg border border-background-200 bg-background-100 p-6">
            <h3 className="font-heading text-base font-bold text-foreground-950">
              评论区小提示
            </h3>
            <ul className="mt-4 flex flex-col gap-4">
              {[
                { icon: "ri-file-list-3-line", text: "补充线索时尽量带上时间点，方便别人对照。" },
                { icon: "ri-shield-check-line", text: "对事不对人，不发布未经证实的指控。" },
                { icon: "ri-links-line", text: "优质补充会被整理进对应的合集与名单。" },
              ].map((tip) => (
                <li key={tip.text} className="flex gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-background-200 text-foreground-700">
                    <i className={`${tip.icon} text-base`} />
                  </span>
                  <span className="text-[13px] leading-relaxed text-foreground-600">
                    {tip.text}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>
    </section>
  );
}